import React from "react";
import { useQuery } from "@tanstack/react-query";
import apiService from "../../services/api";
import { formatCurrency } from "../../utils/currencyUtils";

const LINE_TYPE_LABEL = {
  late_report_penalty: "Late Report Penalty",
  regular_cancel_penalty: "Regular Cancel Penalty",
  regular_noshow_penalty: "Regular No-Show Penalty",
  severe_cancel_penalty: "Severe Cancel Penalty",
  severe_noshow_penalty: "Severe No-Show Penalty",
  adjustment: "Adjustment",
};

const s = {
  card: {
    background: "white", borderRadius: "0.5rem", border: "1px solid #e2e8f0",
    boxShadow: "0 1px 3px rgba(0,0,0,0.08)", padding: "1rem", marginTop: "1rem",
  },
  heading: { fontSize: "1rem", fontWeight: "600", color: "#001272", marginBottom: "0.4rem" },
  empty: { fontSize: "0.85rem", color: "#9ca3af", fontStyle: "italic", margin: "0.5rem 0" },
  tutorGroup: { marginBottom: "0.75rem" },
  tutorName: { fontSize: "0.82rem", fontWeight: "600", color: "#001272", marginBottom: "0.2rem" },
  itemRow: { fontSize: "0.8rem", padding: "0.2rem 0 0.2rem 0.75rem", color: "#374151", display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: "0.5rem" },
  itemDesc: { flex: 1 },
  amount: { fontWeight: "600", whiteSpace: "nowrap" },
};

const LockedPayrollAdjustmentsPanel = ({ yearMonth }) => {
  const { data: lineItems = [], isLoading } = useQuery({
    queryKey: ["allPayrollLineItems", yearMonth],
    queryFn: () => apiService.getAllPayrollLineItems(yearMonth),
    staleTime: 5 * 60 * 1000,
  });

  const adjustments = lineItems.filter((li) => li.lineType === "adjustment");
  const waivedPenalties = lineItems.filter((li) => li.penaltyWaived);

  const groupByTutor = (items) => {
    const map = new Map();
    items.forEach((li) => {
      const key = li.canonicalTutorId || "unknown";
      if (!map.has(key)) map.set(key, { tutorName: li.tutorName || "Unknown", items: [] });
      map.get(key).items.push(li);
    });
    return Array.from(map.values());
  };

  if (isLoading) return null;
  if (adjustments.length === 0 && waivedPenalties.length === 0) return null;

  return (
    <div style={s.card}>
      <h3 style={s.heading}>Adjustments &amp; Waivers</h3>

      {/* Post-Generation Adjustments */}
      {adjustments.length > 0 ? (
        <div style={{ marginBottom: waivedPenalties.length > 0 ? "1rem" : 0 }}>
          <div style={{ fontSize: "0.85rem", fontWeight: "600", color: "#7e22ce", marginBottom: "0.4rem" }}>
            Manual Adjustments ({adjustments.length})
          </div>
          {groupByTutor(adjustments).map((group) => (
            <div key={group.tutorName} style={s.tutorGroup}>
              <div style={s.tutorName}>{group.tutorName}</div>
              {group.items.map((li) => (
                <div key={li.id} style={s.itemRow}>
                  <span style={s.itemDesc}>{li.description || "—"}</span>
                  <span style={{ ...s.amount, color: li.amount != null && li.amount >= 0 ? "#166534" : "#dc2626" }}>
                    {formatCurrency(li.amount)}
                  </span>
                </div>
              ))}
            </div>
          ))}
        </div>
      ) : (
        <p style={s.empty}>No manual adjustments.</p>
      )}

      {/* Penalty Waivers (Fee Waiving) */}
      {waivedPenalties.length > 0 && (
        <div>
          <div style={{ fontSize: "0.85rem", fontWeight: "600", color: "#0369a1", marginBottom: "0.4rem" }}>
            Fee Waivers ({waivedPenalties.length})
          </div>
          {groupByTutor(waivedPenalties).map((group) => (
            <div key={group.tutorName} style={s.tutorGroup}>
              <div style={s.tutorName}>{group.tutorName}</div>
              {group.items.map((li) => (
                <div key={li.id} style={s.itemRow}>
                  <span style={s.itemDesc}>
                    {LINE_TYPE_LABEL[li.lineType] || li.lineType}
                    {li.tcAppointmentId && <span style={{ color: "#9ca3af", fontSize: "0.72rem" }}> · TC #{li.tcAppointmentId}</span>}
                    {li.appointmentTopic && <span style={{ color: "#9ca3af", fontSize: "0.72rem" }}> · {li.appointmentTopic}</span>}
                  </span>
                  <span style={{ ...s.amount, color: "#0369a1" }}>
                    Waived
                  </span>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default LockedPayrollAdjustmentsPanel;
